import React from 'react';
import PropTypes from 'prop-types';
import { adopt } from 'react-adopt';
import { paidAdStatus } from 'config';
import { Mutation, Query } from 'components/common/Query';
import gql from 'graphql-tag';
import { withTranslation } from 'react-i18next';
import {
  PUBLISH_ADS_MUTATION,
  REVIEW_ADS_MUTATION,
} from 'components/PaidAd/List';
import PaidAdReview from './PaidAdReview';

/**
 * Query to fetch ads from api
 */
export const PAIDADS_REVIEW_QUERY = gql`
  query PaidAdsList($status: [FilterAutoPaidAdStatus]) {
    paidAds(status: $status) {
      edges {
        node {
          id
          paidAdType
          startDate
          endDate
          owner {
            id
            fullname
          }
          manager {
            id
            fullname
            userType
          }
          hasPromotion
          duration {
            years
            months
            days
            hours
            minutes
            seconds
          }
          status
          ad {
            id
            title
            subcategory {
              id
              title
              parentCategory {
                id
                title
              }
            }
            readyImages {
              id
              urls {
                high
                thumb
              }
            }
            updatedOnToOrder
            flagCount
            status
            reviewed
            isPublished
            updateCount
          }
          commission
          online
          ... on AdminPromotedAdType {
            locationScope
            userPrice
          }
          ... on AdminAutoPaidAdType {
            frequency {
              years
              months
              days
              hours
              minutes
              seconds
            }
            schedule
            userPrice
          }
        }
      }
    }
  }
`;

class PaidAdReviewContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      itemProcessing: [],
      failedIds: [],
      notification: {},
    };
    this.Compose = adopt({
      paidAdsReview: this.loadPaidAdsReview,
      mutateAdReview: this.adReview,
      mutateAdPublish: this.adPublish,
    });
    this.fetchMore = () => {};
  }

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  loadPaidAdsReview = ({ render }) => (
    <Query
      query={PAIDADS_REVIEW_QUERY}
      variables={{ status: paidAdStatus.PENDING }}
      notifyOnNetworkStatusChange
      fetchPolicy="cache-and-network"
    >
      {({ data, loading, fetchMore }) => {
        this.fetchMore = fetchMore;
        return render({ data, loading });
      }}
    </Query>
  );

  /**
   * @description
   * Mutation review callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  adReview = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <Mutation
        mutation={REVIEW_ADS_MUTATION}
        onCompleted={this.mutationCompleted('reviewAd', itemProcessing)}
        update={() => {
          this.fetchMore({
            updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
          });
        }}
      >
        {handleAdReview => render({ handleAdReview })}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation publish callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  adPublish = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <Mutation
        mutation={PUBLISH_ADS_MUTATION}
        onCompleted={this.mutationCompleted('publishAd', itemProcessing)}
        update={() => {
          this.fetchMore({
            updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
          });
        }}
      >
        {handleAdPublish => render({ handleAdPublish })}
      </Mutation>
    );
  };

  /**
   * @description
   * OnComplete delete mutation.
   *
   * @param {Object} field - Target field of mutation.
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @returns {undefined} - No returns value.
   */
  mutationCompleted = (field, itemProcessing) => async data => {
    const { t } = this.props;
    const failedIds = itemProcessing.filter(
      item => !data[field].successIds.includes(item),
    );

    this.setState({
      itemProcessing: [],
      failedIds,
      notification:
        failedIds.length > 0
          ? {
              type: 'failed',
              message: t(`common.failed_bulk_action`, {
                count: failedIds.length,
              }),
            }
          : {},
    });
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Close notification.
   * @returns {undefined} - No returns value.
   */
  handleCloseNotification = () => {
    this.setState({ notification: {} });
  };

  /**
   * @description
   * Render PaidAdsList component.
   */
  render() {
    const { itemProcessing, failedIds, notification } = this.state;
    return (
      <this.Compose>
        {({
          paidAdsReview: { loading, data },
          mutateAdReview: { handleAdReview },
          mutateAdPublish: { handleAdPublish },
        }) => (
          <PaidAdReview
            data={data || {}}
            refresh={this.handleRefresh}
            loading={data ? Object.keys(data).length === 0 : true}
            fullLoading={loading}
            adReview={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleAdReview({ variables: { ids: items } });
            }}
            adPublish={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleAdPublish({ variables: { ids: items } });
            }}
            failedIds={failedIds}
            notification={notification}
            closeNotification={this.handleCloseNotification}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * PaidAdReviewContainer PropTypes
 * @param initialFilters - prop initial Filters to apply
 */

PaidAdReviewContainer.propTypes = {
  t: PropTypes.func.isRequired,
  initialFilters: PropTypes.shape({}).isRequired,
};

export default withTranslation()(PaidAdReviewContainer);
